import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CACHE_KEY = 'agent9_brand_config';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const DEFAULTS = {
  brand_name: 'Agent9',
  brand_tagline: 'AI-Powered Real Estate Assistant',
  brand_logo_url: '',
  brand_favicon_url: '',
  brand_primary_color: '#10b981',
  brand_accent_color: '#0d9488',
  support_email: 'support@agent9.me',
  powered_by_text: 'Powered by AsterPOS Corp.',
  powered_by_url: 'https://125cents.com',
  signup_enabled: true,
  feature_flags: {},
};

const BrandContext = createContext(DEFAULTS);

export function BrandProvider({ children }) {
  const [brand, setBrand] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(CACHE_KEY));
      if (cached && Date.now() - cached._ts < CACHE_TTL) return cached;
    } catch { /* ignore */ }
    return DEFAULTS;
  });

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/v1/public/config`).then(({ data }) => {
      if (cancelled) return;
      const cfg = { ...DEFAULTS, ...data, _ts: Date.now() };
      setBrand(cfg);
      localStorage.setItem(CACHE_KEY, JSON.stringify(cfg));

      // Apply CSS custom properties for theme colors
      const root = document.documentElement;
      root.style.setProperty('--brand-primary', cfg.brand_primary_color);
      root.style.setProperty('--brand-accent', cfg.brand_accent_color);
    }).catch(() => { /* use defaults */ });
    return () => { cancelled = true; };
  }, []);

  // Convenience: logo falls back to local asset
  const logoUrl = brand.brand_logo_url || '/assets/agent9-icon.png';
  const name = brand.brand_name || 'Agent9';

  return (
    <BrandContext.Provider value={{ ...brand, logoUrl, name }}>
      {children}
    </BrandContext.Provider>
  );
}

export const useBrand = () => useContext(BrandContext);
